import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useRoles, type Role } from '../hooks/useRoles';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@marine/ui/dialog';
import { Button } from '@marine/ui/button';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Switch } from '@marine/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@marine/ui/select';
import { ManufacturerMultiCombobox } from '@/components/combobox/ManufacturerMultiCombobox';
import { useCreateEmployee, useUpdateEmployee } from '../hooks/useEmployees';
import { useLocations } from '../hooks/useLocations';
import type { Employee } from '../state/tenantAuth';

interface EmployeeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employee?: Employee | null;
  onCreated?: (id: string) => void;
}

interface EmployeeFormData {
  first_name: string;
  last_name: string;
  email: string;
  password?: string;
  role: string;
  location_id?: string; // ULID string from Select
  // #733 — names this employee a lead dispatcher (notified for every
  // ownerless enquiry). See DispatcherResolver.
  is_lead_dispatcher?: boolean;
  /** #705 — lead-routing availability. */
  is_available_for_leads?: boolean;
  lead_unavailable_until?: string | null;
  /** #706 — brand specialties (canonical manufacturer ids, full-replace). */
  specialty_ids?: string[];
}

export function EmployeeFormDialog({
  open,
  onOpenChange,
  employee,
  onCreated,
}: EmployeeFormDialogProps) {
  const isEditing = !!employee;
  const { mutate: createEmployee, isPending: isCreating } = useCreateEmployee();
  const { mutate: updateEmployee, isPending: isUpdating } = useUpdateEmployee();
   
  const { data: locations, isLoading: _isLoadingLocations } = useLocations();
  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<EmployeeFormData>({
    defaultValues: {
      role: 'employee',
      is_lead_dispatcher: false,
      is_available_for_leads: true,
      specialty_ids: [],
    },
  });

  const roleFn = watch('role');
  const locationFn = watch('location_id');

  useEffect(() => {
    if (open) {
      if (employee) {
        // Editing
        reset({
          first_name: employee.first_name,
          last_name: employee.last_name,
          email: employee.email,
          role: employee.role,
          location_id: employee.location_id?.toString() || '',
          // #893 — this dialog has no phone/job_title inputs; seeding and
          // submitting them (as '') silently wiped the stored values on save.
          // Fields the dialog doesn't edit must never appear in the payload.
          is_lead_dispatcher: employee.is_lead_dispatcher ?? false,
          is_available_for_leads: employee.is_available_for_leads ?? true,
          lead_unavailable_until: employee.lead_unavailable_until ?? '',
          specialty_ids: employee.specialties?.map((s) => s.manufacturer_id) ?? [],
        });
      } else {
        // Creating
        reset({
          first_name: '',
          last_name: '',
          email: '',
          password: '',
          role: 'employee',
          location_id: '',
          is_lead_dispatcher: false,
          is_available_for_leads: true,
          lead_unavailable_until: '',
          specialty_ids: [],
        });
      }
    }
  }, [open, employee, reset]);

  const onSubmit = (data: EmployeeFormData) => {
    // #705 — an available rep never carries a return date; an empty date
    // input means "no return date set" (null), not the empty string.
    const availability = {
      is_available_for_leads: data.is_available_for_leads ?? true,
      lead_unavailable_until:
        (data.is_available_for_leads ?? true) ? null : data.lead_unavailable_until || null,
    };

    // Normalize optional location_id (ULID string)
    const payload = {
      ...data,
      ...availability,
      location_id:
        data.location_id && data.location_id !== 'GLOBAL_SCOPE'
          ? data.location_id
          : undefined,
    };

    if (isEditing && employee) {
      const { password, ...updateData } = payload;
      // Only include password if provided
      const finalData = password ? { ...updateData, password } : updateData;
      
      updateEmployee(
        { id: employee.id, ...finalData },
        {
          onSuccess: () => onOpenChange(false),
        }
      );
    } else {
      if (!payload.password) {
        // Handle error? Password required for create.
        // But let's assume form validation handles it or backend errors.
        // We'll require it in form for create.
      }
      createEmployee(payload as Parameters<typeof createEmployee>[0], {
        onSuccess: (created) => {
          onOpenChange(false);
          if (created?.id) onCreated?.(String(created.id));
        },
      });
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Employee' : 'New Employee'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update employee access details.'
              : 'Add a new employee to the system.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                {...register('first_name', { required: 'First name is required' })}
              />
              {errors.first_name && <p className="text-sm text-destructive">{errors.first_name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                {...register('last_name', { required: 'Last name is required' })}
              />
              {errors.last_name && <p className="text-sm text-destructive">{errors.last_name.message}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              {...register('email', { required: 'Email is required' })}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          {(!isEditing || (isEditing && watch('password'))) && (
             // Show password field always for create, optional for edit
             // But simpler to just show it.
             <div className="space-y-2">
                <Label htmlFor="password">{isEditing ? 'New Password (Optional)' : 'Password *'}</Label>
                <Input
                  id="password"
                  type="password"
                  {...register('password', { required: !isEditing ? 'Password is required' : false })}
                />
                 {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
             </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="role">Role *</Label>
            <Select
              value={roleFn}
              onValueChange={(val) => setValue('role', val)}
              disabled={isLoadingRoles}
            >
              <SelectTrigger>
                 <SelectValue placeholder={isLoadingRoles ? "Loading..." : "Select a role"} />
              </SelectTrigger>
              <SelectContent>
                {roles?.map((role: Role) => (
                  <SelectItem key={role.id} value={role.name}>
                    {role.name.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </SelectItem>
                ))}
                {!isLoadingRoles && (!roles || roles.length === 0) && (
                   <SelectItem value="none" disabled>No roles found</SelectItem>
                )}
              </SelectContent>
            </Select>
            {errors.role && <p className="text-sm text-destructive">{errors.role.message}</p>}
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="is_lead_dispatcher">Lead dispatcher</Label>
              <p className="text-sm text-muted-foreground">
                Notified whenever an inquiry arrives with no owner, so they can assign it.
              </p>
            </div>
            <Switch
              id="is_lead_dispatcher"
              checked={watch('is_lead_dispatcher') ?? false}
              onCheckedChange={(checked) => setValue('is_lead_dispatcher', checked)}
            />
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="is_available_for_leads">Available for leads</Label>
              <p className="text-sm text-muted-foreground">
                Out-of-office reps are removed from every routing rotation.
              </p>
            </div>
            <Switch
              id="is_available_for_leads"
              checked={watch('is_available_for_leads') ?? true}
              onCheckedChange={(checked) => setValue('is_available_for_leads', checked)}
            />
          </div>

          {!(watch('is_available_for_leads') ?? true) && (
            <div className="space-y-1">
              <Label htmlFor="lead_unavailable_until">Back on (optional)</Label>
              <Input
                id="lead_unavailable_until"
                type="date"
                {...register('lead_unavailable_until')}
              />
              <p className="text-sm text-muted-foreground">
                Returns to the rotation automatically at the start of this day.
              </p>
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="specialty_ids">Brand specialties</Label>
            <ManufacturerMultiCombobox
              id="specialty_ids"
              value={watch('specialty_ids') ?? []}
              onChange={(ids) => setValue('specialty_ids', ids)}
            />
            <p className="text-sm text-muted-foreground">
              Brands this rep specialises in. Specialist deals route to them first
              once auto-assignment is on.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Select
              value={locationFn}
              onValueChange={(val) => setValue('location_id', val)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="GLOBAL_SCOPE">All Locations / None</SelectItem>
                {locations?.map((loc) => (
                  <SelectItem key={loc.id} value={loc.id.toString()}>
                    {loc.name} {loc.is_main ? '(Main)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Optional. Admins can access all locations.
            </p>
          </div>

          <DialogFooter>
             <Button
               type="button"
               variant="outline"
               onClick={() => onOpenChange(false)}
               disabled={isPending}
             >
               Cancel
             </Button>
             <Button type="submit" disabled={isPending}>
               {isPending ? 'Saving...' : 'Save'}
             </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
